package co.uk.paxes.pinpoint.web

import kotlinx.browser.window
import kotlin.js.Json
import kotlin.js.json

/**
 * Game settings data class.
 * Matches the design document GameSettings interface.
 */
data class GameSettings(
    val showCurrentPinScore: Boolean = false,
    val showCurrentHandScore: Boolean = false,
    val soundEnabled: Boolean = true,
    val aiSpeed: Int = 1000, // Default 1000ms delay
    val maxUndosPerHand: Int = 4 // Default 4 undos per hand (using credit system)
)

/**
 * SettingsManager handles user preferences and settings persistence.
 * Implements the interface defined in the design document.
 * 
 * Requirements: 12.4, 12.5
 */
object SettingsManager {
    private const val STORAGE_KEY = "pinpoint_settings"
    
    private var currentSettings = GameSettings()
    
    /**
     * Initialize settings by loading from local storage.
     * Should be called on app startup.
     */
    fun initialize() {
        currentSettings = loadSettings()
        console.log("SettingsManager initialized: showPinScore=${currentSettings.showCurrentPinScore}, showHandScore=${currentSettings.showCurrentHandScore}, maxUndos=${currentSettings.maxUndosPerHand}")
    }
    
    /**
     * Get current settings.
     * 
     * Requirements: 12.4
     */
    fun getSettings(): GameSettings = currentSettings
    
    /**
     * Update a setting by key.
     * Automatically saves to local storage after update.
     * 
     * Requirements: 12.4
     */
    fun setSetting(key: String, value: Any) {
        currentSettings = when (key) {
            "showCurrentPinScore" -> currentSettings.copy(showCurrentPinScore = value as Boolean)
            "showCurrentHandScore" -> currentSettings.copy(showCurrentHandScore = value as Boolean)
            "soundEnabled" -> currentSettings.copy(soundEnabled = value as Boolean)
            "aiSpeed" -> currentSettings.copy(aiSpeed = (value as Number).toInt())
            "maxUndosPerHand" -> currentSettings.copy(maxUndosPerHand = (value as Number).toInt().coerceIn(0, 10))
            else -> {
                console.warn("Unknown setting key: $key")
                currentSettings
            }
        }
        saveSettings()
    }
    
    /**
     * Save current settings to storage (PlayGama or localStorage).
     * 
     * Requirements: 12.4
     */
    fun saveSettings() {
        try {
            val jsonObj = json(
                "showCurrentPinScore" to currentSettings.showCurrentPinScore,
                "showCurrentHandScore" to currentSettings.showCurrentHandScore,
                "soundEnabled" to currentSettings.soundEnabled,
                "aiSpeed" to currentSettings.aiSpeed,
                "maxUndosPerHand" to currentSettings.maxUndosPerHand
            )
            val jsonString = JSON.stringify(jsonObj)
            PlayGamaStorageManager.setItem(STORAGE_KEY, jsonString)
            console.log("Settings saved: $jsonString")
        } catch (e: Exception) {
            console.error("Failed to save settings: ${e.message}")
        }
    }
    
    /**
     * Load settings from storage (PlayGama or localStorage).
     * Returns default settings if none exist or on error.
     * 
     * Requirements: 12.5
     */
    fun loadSettings(): GameSettings {
        return try {
            val jsonString = PlayGamaStorageManager.getItem(STORAGE_KEY)
            if (jsonString != null) {
                val parsed = JSON.parse<Json>(jsonString)
                GameSettings(
                    showCurrentPinScore = parsed["showCurrentPinScore"] as? Boolean ?: false,
                    showCurrentHandScore = parsed["showCurrentHandScore"] as? Boolean ?: false,
                    soundEnabled = parsed["soundEnabled"] as? Boolean ?: true,
                    aiSpeed = (parsed["aiSpeed"] as? Number)?.toInt() ?: 1000,
                    maxUndosPerHand = (parsed["maxUndosPerHand"] as? Number)?.toInt()?.coerceIn(0, 10) ?: 4
                )
            } else {
                console.log("No saved settings found, using defaults")
                GameSettings()
            }
        } catch (e: Exception) {
            console.error("Failed to load settings: ${e.message}")
            GameSettings()
        }
    }
    
    /**
     * Check if pin score display is enabled.
     */
    fun isShowCurrentPinScoreEnabled(): Boolean = currentSettings.showCurrentPinScore
    
    /**
     * Check if hand score display is enabled.
     */
    fun isShowCurrentHandScoreEnabled(): Boolean = currentSettings.showCurrentHandScore
    
    /**
     * Check if sound is enabled.
     */
    fun isSoundEnabled(): Boolean = currentSettings.soundEnabled
    
    /**
     * Get max undos per hand setting.
     */
    fun getMaxUndosPerHand(): Int = currentSettings.maxUndosPerHand
}
